// theme-sources/js/core-ui/components/predictive-search.js
import { sectionLogger } from "./_logger";

const DEBUG = false;
const logger = sectionLogger("PredictiveSearch", DEBUG);

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.controller = null;
    this.onQuery = this.onQuery.bind(this);

    this.COLLECTIONS_BY_TAGS = [];
    this.COLLECTIONS_READY = false;
    this.COLLECTION_MATCH_MODE = "OR";

    this.cache = new Map();

    // Navigation clavier
    this.activeIndex = -1;
    this.items = [];
    this.onKeyDown = this.onKeyDown.bind(this);
    this.keyboardNav = false;
  }

  connectedCallback() {
    this.panel = this.querySelector("[data-panel]");
    this.results = this.querySelector("[data-results]");
    this.i18nEl = this.querySelector("[data-i18n]");

    logger.info("panel", this.querySelector("[data-panel]"));
    logger.info("results", this.querySelector("[data-results]"));

    //this.loadCollections();

    this.boundQuery = this.onQuery.bind(this);
    document.addEventListener("predictive-search:query", this.boundQuery);
    // document.addEventListener("predictive-search:query", this.onQuery);

    this._onDocClick = (e) => {
      // clique en dehors du form + panel => close
      // (formEl et panel ne sont pas dans le même composant, donc on check le parent bloc)
      const root = this.parentElement; // même parent que <search-form>
      if (!root) return;

      if (!root.contains(e.target)) {
        this.panel?.classList.add("hidden");
        this.lastQuery = null;
      }
    };

    document.addEventListener("click", this._onDocClick);
    document.addEventListener("keydown", this.onKeyDown);
    logger.info("Init");
  }

  disconnectedCallback() {
    //document.removeEventListener("predictive-search:query", this.onQuery);
    document.removeEventListener("predictive-search:query", this.boundQuery);
    document.removeEventListener("click", this._onDocClick);
    document.removeEventListener("keydown", this.onKeyDown);
  }

  async onQuery(e) {
    if (!this.COLLECTIONS_READY) {
      await this.loadCollections();
    }
    console.log("MOBILE EVENT RECEIVED", e.detail);
    const formEl = e.target?.closest?.("search-form");
    if (!formEl) return;

    const container = formEl.closest("mobile-search, .site-header, body");
    //const owner = formEl.parentElement?.querySelector("predictive-search") || container?.querySelector("predictive-search");
    const owner = formEl.parentElement?.querySelector("predictive-search");
    if (owner !== this) return;

    this.panel = this.panel || this.querySelector("[data-panel]");
    this.results = this.results || this.querySelector("[data-results]");
    this.i18nEl = this.i18nEl || this.querySelector("[data-i18n]");

    if (!this.panel || !this.results) return;

    const query = (e.detail?.query || "").trim();

    console.log("MOBILE QUERY:", query);

    if (query.length < 2) {
      this.panel.classList.add("hidden");
      this.results.innerHTML = "";
      this.lastQuery = null;
      return;
    }

    // 🔥 Evite requête identique consécutive
    if (this.lastQuery === query) return;
    this.lastQuery = query;

    // 🔥 CACHE HIT
    if (this.cache.has(query)) {
      const cachedResults = this.cache.get(query);
      this.renderResults(query, cachedResults);
      this.panel.classList.remove("hidden");
      return;
    }

    // --- Abort previous request ---
    if (this.controller) this.controller.abort();
    this.controller = new AbortController();

    const baseUrl = window.theme?.routes?.predictiveSearchUrl;
    if (!baseUrl) return;

    const url = `${baseUrl}.json?q=${encodeURIComponent(
      query,
    )}&resources[type]=collection,article&resources[limit]=10`;

    try {
      const res = await fetch(url, { signal: this.controller.signal });
      if (!res.ok) return;

      const data = await res.json();
      const results = data?.resources?.results || {};

      // 🔥 STOCKAGE EN CACHE
      this.cache.set(query, results);

      if (this.cache.size > 20) {
        const firstKey = this.cache.keys().next().value;
        this.cache.delete(firstKey);
      }

      this.renderResults(query, results);
      this.panel.classList.remove("hidden");
    } catch (err) {
      if (err?.name === "AbortError") return;
      console.error(err);
    }
  }

  async loadCollections() {
    if (!window.theme?.routes?.collections) {
      logger.warn("Missing collections route");
      this.COLLECTIONS_READY = true;
      return;
    }
    try {
      const res = await fetch(
        `${window.theme.routes.collections}/all?view=search`,
      );
      const data = await res.json();
      this.COLLECTIONS_BY_TAGS = data.collections || [];
    } catch (e) {
      console.error(e);
    } finally {
      this.COLLECTIONS_READY = true;
    }
  }

  renderResults(query, r) {
    const shopifyCollections = r.collections || [];
    const shopifyArticles = r.articles || [];

    let metafieldCollections = [];
    let collectionsToRender = [];
    let articlesToRender = shopifyArticles.slice(0, 8);
    let usedFallback = false;

    if (this.COLLECTIONS_READY) {
      metafieldCollections =
        this.COLLECTION_MATCH_MODE === "AND"
          ? this.matchCollectionsAND(query, this.COLLECTIONS_BY_TAGS)
          : this.matchCollectionsOR(query, this.COLLECTIONS_BY_TAGS);
    }

    // ===== NORMAL BEHAVIOR =====
    if (this.COLLECTION_MATCH_MODE === "AND") {
      collectionsToRender = metafieldCollections;
    } else {
      collectionsToRender = mergeCollections(
        metafieldCollections,
        shopifyCollections,
      );
    }

    // ===== FALLBACK 1 : AND → OR =====
    const hasResults =
      collectionsToRender.length > 0 || articlesToRender.length > 0;

    if (!hasResults && this.COLLECTION_MATCH_MODE === "AND") {
      collectionsToRender = mergeCollections(
        this.matchCollectionsOR(query, this.COLLECTIONS_BY_TAGS),
        shopifyCollections,
      );
      usedFallback = true;
    }

    // ===== FALLBACK 2 : métier uniquement (mot-clé fort) =====
    if (
      collectionsToRender.length === 0 &&
      articlesToRender.length === 0 &&
      this.COLLECTIONS_READY
    ) {
      const q = normalize(query);

      collectionsToRender = this.COLLECTIONS_BY_TAGS.filter((c) =>
        (c.tags || []).some((tag) => normalize(tag).startsWith(q)),
      );

      usedFallback = true;
    }

    const fallbackNotice =
      usedFallback && this.i18nEl?.dataset?.noResultsHtml
        ? `
              <div class="col-span-12 text-sm text-gray-500">
                ${this.i18nEl.dataset.noResultsHtml.replace("__TERM__", `<strong>${query}</strong>`)}
              </div>
            `
        : "";

    this.results.innerHTML = `
      <div class="grid grid-cols-12 gap-8">
        ${fallbackNotice}
        ${collectionsColumn(collectionsToRender.slice(0, 5))}
        ${articlesColumn(articlesToRender)}
      </div>
    `;

    this.items = Array.from(this.results.querySelectorAll("a"));

    this.activeIndex = -1;
    this.keyboardNav = false;
  }

  // OR : un seul mot-clé métier suffit
  matchCollectionsOR(query, collections) {
    const q = normalize(query);
    if (!q) return [];
    return (collections || []).filter((c) => {
      const tags = Array.isArray(c.tags) ? c.tags : [];
      return tags.some((tag) => {
        const t = normalize(tag);
        return t.startsWith(q) || q.startsWith(t);
      });
    });
  }

  // AND : tous les mots doivent être présents dans ziggy.tags
  matchCollectionsAND(query, collections) {
    const words = getQueryWords(query);
    if (!words.length) return [];
    return (collections || []).filter((c) => {
      const tags = Array.isArray(c.tags) ? c.tags : [];
      const normalizedTags = tags.map(normalize);
      return words.every((word) =>
        normalizedTags.some(
          (tag) => tag.startsWith(word) || word.startsWith(tag),
        ),
      );
    });
  }

  onKeyDown(e) {
    if (!this.panel || this.panel.classList.contains("hidden")) return;

    if (!this.items.length) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        this.moveActive(1);
        break;

      case "ArrowUp":
        e.preventDefault();
        this.moveActive(-1);
        break;

      case "Enter":
        e.preventDefault(); // bloque submit + comportement natif

        // Si le panel est fermé → on l’ouvre (et on reste sur la page)
        if (this.panel.classList.contains("hidden")) {
          this.panel.classList.remove("hidden");
          return;
        }

        // Panel déjà ouvert :
        // On n'ouvre un lien que si l'utilisateur a explicitement navigué avec les flèches
        if (
          this.keyboardNav &&
          this.activeIndex >= 0 &&
          this.items[this.activeIndex]
        ) {
          this.items[this.activeIndex].click(); // navigation volontaire
        }

        // Sinon : Enter ne fait rien (reste sur place)
        return;

      case "Escape":
        this.panel.classList.add("hidden");
        this.activeIndex = -1;
        break;
    }
  }

  moveActive(direction) {
    this.keyboardNav = true;
    this.activeIndex += direction;

    if (this.activeIndex < 0) this.activeIndex = this.items.length - 1;
    if (this.activeIndex >= this.items.length) this.activeIndex = 0;

    this.updateActiveItem();
  }

  updateActiveItem() {
    this.items.forEach((item, index) => {
      if (index === this.activeIndex) {
        item.classList.add("bg-primary/10");
      } else {
        item.classList.remove("bg-primary/10");
      }
    });

    const activeItem = this.items[this.activeIndex];
    if (activeItem) {
      activeItem.scrollIntoView({ block: "nearest" });
    }
  }
}
if (!customElements.get("predictive-search")) {
  customElements.define("predictive-search", PredictiveSearch);
}

/* ======================================================
        COLLECTIONS COLUMN (LEFT)
      ======================================================= */
function collectionsColumn(items = []) {
  if (!items.length) return "";

  return `
        <div class="col-span-12 lg:col-span-3 order-1">
          <h3 class="mb-4 text-sm font-semibold uppercase text-gray-500">
            Collections
          </h3>

          <ul class="space-y-4">
            ${items
              .map(
                (c) => `
              <li>
                <a
                  href="${c.url}"
                  class="group flex items-center justify-between gap-4 rounded-lg border border-primary/50 overflow-hidden transition hover:shadow-sm"
                >
                  <div class="flex-1 p-2">
                    <div class="text-sm font-title leading-snug">
                      ${escapeHTML(c.title)}
                    </div>
                  </div>

                  <div class="h-20 w-20 shrink-0 overflow-hidden bg-primary/10">
                    ${
                      c.featured_image?.url
                        ? `
                      <img
                        src="${c.featured_image.url}"
                        alt="${c.featured_image.alt || c.title}"
                        class="h-full w-full object-cover transition group-hover:scale-105"
                      >
                    `
                        : ""
                    }
                  </div>
                </a>
              </li>
            `,
              )
              .join("")}
          </ul>
        </div>
      `;
}

/* ======================================================
        ARTICLES COLUMN (RIGHT)
      ======================================================= */
function articlesColumn(items = []) {
  if (!items.length) return "";

  return `
        <div class="col-span-12 lg:col-span-9 order-2">
          <h3 class="mb-4 text-sm font-semibold uppercase text-gray-500">
            Articles
          </h3>
          <div class="grid grid-cols-2 lg:grid-cols-4 gap-6">
            ${items
              .map(
                (a) => `
              <a href="${a.url}" class="group block">
                <div class="aspect-[4/3] overflow-hidden rounded-xl bg-primary/10">
                  ${
                    a.image
                      ? `
                    <img
                      src="${a.image}"
                      class="h-full w-full object-cover transition group-hover:scale-105"
                    >
                  `
                      : ""
                  }
                </div>
                <div class="mt-3 text-sm font-medium leading-snug">
                  ${escapeHTML(a.title)}
                </div>
              </a>
            `,
              )
              .join("")}
          </div>
        </div>
      `;
}

function escapeHTML(str) {
  return String(str).replace(
    /[&<>"']/g,
    (m) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#039;",
      })[m],
  );
}

function normalize(str) {
  return String(str || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function getQueryWords(query) {
  return normalize(query).split(/\s+/).filter(Boolean);
}

function mergeCollections(primary = [], secondary = []) {
  const seen = new Set();

  return [...primary, ...secondary].filter((c) => {
    if (!c || !c.url) return false;
    if (seen.has(c.url)) return false;
    seen.add(c.url);
    return true;
  });
}
